import React, { CSSProperties, useLayoutEffect, useRef } from 'react'

import { SPLIT_FRACTION_VAR, useSplitFraction } from '../../useSplitFraction'
import { CompareMode } from '../../utils'
import {
  Actions,
  OverlayButton,
  SplitButton,
  SwapButton,
} from './EntityCompareActions.styles'

interface EntityCompareActionsProps {
  /** Whether a line across it would show anything - see `canSplitEntity`. */
  canSplit: boolean
  /** The mode that is on. */
  mode: CompareMode
  /** Which side the canvas draws the compared version on, so the button agrees. */
  sourceOnRight: boolean
  onToggleMode(mode: CompareMode): void
}

/**
 * The modes a selected changed entity can be read with.
 *
 * Each mode is asked for rather than applied by selecting: selecting is how a
 * reader looks at something, and all three want that same click. Picking one
 * leaves the others reachable.
 *
 * Only the ones that would do something are here. A greyed-out control still
 * asks to be read and reasoned about, and these sit on the canvas over the
 * thing the reader is looking at, where there is no room for that.
 */
export function EntityCompareActions({
  canSplit,
  mode,
  sourceOnRight,
  onToggleMode,
}: EntityCompareActionsProps) {
  const isSplit = mode === 'split'
  const isSwapped = mode === 'swap'
  const isOverlaid = mode === 'overlay'

  // Only this button follows the line, so only this button renders as it moves
  const fraction = useSplitFraction()

  const splitRef = useRef<HTMLButtonElement>(null)
  const swapRef = useRef<HTMLButtonElement>(null)

  // Read while rendering, while Split is still in the page: once it is taken
  // out, focus has already fallen back to the document
  const splitLosesFocus =
    !canSplit &&
    splitRef.current !== null &&
    splitRef.current === document.activeElement

  useLayoutEffect(() => {
    if (splitLosesFocus) swapRef.current?.focus()
  }, [splitLosesFocus])

  return (
    <Actions
      role="group"
      aria-label="How to compare this change"
      data-testid="compare-entity-actions"
      // The overlay these sit in opens its entity on double-click, and turning a
      // mode on and straight off again would otherwise do it
      onDoubleClick={event => event.stopPropagation()}
      // And the canvas under them selects whatever a click lands on, which
      // would move the selection the mode was just turned on for
      onClick={event => event.stopPropagation()}
    >
      {canSplit && (
        <SplitButton
          ref={splitRef}
          type="button"
          $active={isSplit}
          $sourceOnRight={sourceOnRight}
          style={
            isSplit
              ? ({
                  [SPLIT_FRACTION_VAR]: `${fraction * 100}%`,
                } as CSSProperties)
              : undefined
          }
          aria-pressed={isSplit}
          onClick={() => onToggleMode('split')}
        >
          Split
        </SplitButton>
      )}

      <SwapButton
        ref={swapRef}
        type="button"
        $active={isSwapped}
        aria-pressed={isSwapped}
        onClick={() => onToggleMode('swap')}
      >
        Swap
      </SwapButton>

      <OverlayButton
        type="button"
        $active={isOverlaid}
        aria-pressed={isOverlaid}
        onClick={() => onToggleMode('overlay')}
      >
        Overlay
      </OverlayButton>
    </Actions>
  )
}
